import { type FunctionComponent, type ReactNode, type SyntheticEvent } from "react";
import Grid from "@mui/material/Grid";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportIcon from "@mui/icons-material/Report";
import PersonIcon from "@mui/icons-material/Person";
import ActionIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";

import { StatusChip } from "components/CriticalIncident/components/Shared/StatusChip";

export interface ParticipantGroupAccordionProps {
  label: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  hasErrors?: boolean;
  readonly?: boolean;
  children: ReactNode;
}

const ParticipantGroupIcon: React.FC<{ label: string }> = ({ label }) => {
  const { t } = useTranslation("criticalIncident");
  switch (label) {
    case t("personal_information"):
      return <PersonIcon className="risk-assessments__group-icon" />;
    case t("risk_factors_group"):
      return <ReportIcon className="risk-assessments__group-icon" />;
    case t("actions_taken"):
      return <ActionIcon className="risk-assessments__group-icon" />;
    default:
      return null;
  }
};

export const ParticipantGroupAccordion: FunctionComponent<ParticipantGroupAccordionProps> = ({ label, expanded, onToggle, hasErrors, readonly = false, children }) => {
  const { t } = useTranslation("criticalIncident");

  return (
    <Accordion
      className="risk-assessments__group-accordion"
      elevation={0}
      disableGutters
      expanded={readonly || expanded}
      onChange={readonly ? undefined : (_event: SyntheticEvent, isExpanded: boolean) => onToggle(isExpanded)}
    >
      <AccordionSummary
        expandIcon={readonly ? undefined : <ExpandMoreIcon />}
        className="risk-assessments__group-accordion-summary"
      >
        <ParticipantGroupIcon label={label} />
        <span className="risk-assessments__group-accordion-label">{label}</span>
        {hasErrors && (
          <StatusChip
            status="error"
            icon={<WarningAmberRounded color="error" style={{ fontSize: "1.25rem" }} />}
            label={t("error_required_fields_missing")}
          />
        )}
      </AccordionSummary>
      <AccordionDetails className="risk-assessments__group-accordion-details">
        <Grid container spacing={2}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
